import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import {
  getAddressConfigByCategory as getAddressConfigByCategoryRaw,
  getAddressFieldConfig as getAddressFieldConfigRaw
} from '../../../../shared/cardConfig/helpers';
import { BILL_TO_ADDRESS_FIELDS } from '../../../../shared/fieldConfig/constants';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import AddTaxNumberButton from '../../../AddTaxNumberButton';
import type {
  AddressFieldConfig,
  CardErrorMustContain,
  CardField,
  CardFieldComponent,
  CardFieldProps,
  CardStore,
  TFunc
} from '../../types';

const category = CARD_CATEGORIES.billTo;

// These helpers are authored in JS with JSDoc types that are stricter than
// these call sites' runtime usage; give them explicit (mostly optional)
// argument types and concrete result shapes the cards can rely on. The JS
// inference for `getAddressFieldConfig` is wrong (it reports a boolean return),
// so an `unknown` hop is required to reach the real config shape.
const getAddressFieldConfig = getAddressFieldConfigRaw as unknown as (
  field: string,
  t: TFunc,
  disabled?: boolean,
  maxCharacters?: number | null,
  category?: string,
  isOptional?: boolean,
  controlType?: string
) => AddressFieldConfig;
const getAddressConfigByCategory = getAddressConfigByCategoryRaw as (
  category: string,
  t: TFunc,
  store?: CardStore,
  isCardOptional?: boolean,
  multiShipToIndex?: number,
  disabled?: boolean
) => AddressFieldConfig[];

interface BillToAddressCardProps {
  isReview?: boolean;
  store: CardStore;
  errorMustContain?: CardErrorMustContain;
  fields: CardField[];
  [key: string]: unknown;
}

function TaxNumber1({ t, isReview, store: { editMode: { billTo } = {} } = {}, required, store }: CardFieldProps) {
  const { customer } = store;
  const { [category]: { address: { naturalPerson } = {} } = {}, region = '', countryCode = '' } = customer;
  const isTaxNumber1Required =
    (region.toLowerCase() === 'latam' && countryCode === 'MX' ? !naturalPerson : false) ||
    (region.toLowerCase() === 'emea' && countryCode === 'EU' ? required : false);
  const field = 'taxNumber1';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber1.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    required: isTaxNumber1Required,
    disabled: isReview && !billTo,
    placeholder: t('address.taxNumber1.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber2({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber2';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber2.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber2.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber3({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber3';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber3.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber3.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber4({ t, disabled }: CardFieldProps) {
  const field = 'taxNumber4';
  const name = `${category}.address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber4.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber4.placeholder')
  };

  return <Control {...config} />;
}

function Address({
  isReview,
  store: { editMode: { billTo } = {} } = {},
  t,
  hideSameAs,
  store,
  isCardOptional
}: CardFieldProps) {
  const field = 'address';
  const name = `${category}.${field}`;
  const {
    customer: { region = '', countryCode = '' }
  } = store;
  let cardSpecificFields;
  switch (region.toLowerCase()) {
    case 'latam':
      if (countryCode.toLowerCase() === 'mx') {
        cardSpecificFields = [
          getAddressFieldConfig('naturalPerson', t, isReview && !billTo, null, category, false, CONTROL_TYPE.checkbox),
          getAddressFieldConfig('legalName', t, isReview && !billTo, 105, category, true),
          getAddressFieldConfig('tradeDbaName', t, isReview && !billTo, 35, category)
        ];
      } else {
        cardSpecificFields = [
          getAddressFieldConfig('legalName', t, isReview && !billTo, 105, category, true),
          getAddressFieldConfig('tradeDbaName', t, isReview && !billTo, 35, category)
        ];
      }
      break;
    case 'emea':
      cardSpecificFields = [
        getAddressFieldConfig('tradeDbaName', t, isReview && !billTo, 35, category, true),
        getAddressFieldConfig('legalName', t, isReview && !billTo, 105, category, true)
      ];
      break;
    default:
      cardSpecificFields = [
        // getAddressFieldConfig('coName', t, isReview && !billTo, 40, category),
        getAddressFieldConfig('tradeDbaName', t, isReview && !billTo, 35, category),
        getAddressFieldConfig('legalName', t, isReview && !billTo, 105, category, true)
      ];
      break;
  }
  const config = {
    disabled: isReview,
    category,
    name,
    showSoldTo: true,
    type: CONTROL_TYPE.address,
    hideSameAs,
    label: t(`company.${category}.label`),
    addressFields: [...cardSpecificFields, ...getAddressConfigByCategory('billTo', t, store, isCardOptional)]
  };
  return <Control {...config} />;
}
const controlMap: Record<string, CardFieldComponent> = {
  [BILL_TO_ADDRESS_FIELDS.address]: Address,
  [BILL_TO_ADDRESS_FIELDS.taxNumber1]: TaxNumber1,
  [BILL_TO_ADDRESS_FIELDS.taxNumber2]: TaxNumber2,
  [BILL_TO_ADDRESS_FIELDS.taxNumber3]: TaxNumber3,
  [BILL_TO_ADDRESS_FIELDS.taxNumber4]: TaxNumber4
};

interface RenderTaxNumbersArgs {
  currentTaxNumber: number;
  TotalTaxNumberCount: number;
  disableTaxButton: number;
  fields: CardField[];
  controlMap: Record<string, CardFieldComponent>;
  t: TFunc;
  isReview?: boolean;
  props: BillToAddressCardProps;
  handleAddTaxNumber: (setCurrentTaxNumber: Dispatch<SetStateAction<number>>) => void;
  setCurrentTaxNumber: Dispatch<SetStateAction<number>>;
}

function renderTaxNumbers({
  currentTaxNumber,
  TotalTaxNumberCount,
  disableTaxButton,
  fields,
  controlMap,
  t,
  isReview,
  props,
  handleAddTaxNumber,
  setCurrentTaxNumber // Receive setCurrentTaxNumber as a prop
}: RenderTaxNumbersArgs) {
  return fields.map(({ key, ...fieldProps }: CardField) => {
    const Component = controlMap[key];
    if (key.startsWith('taxNumber')) {
      const index = parseInt(key.slice(-1)) - 1;
      if (index < currentTaxNumber) {
        return (
          <>
            <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />
            {!window.location.href.includes('review') &&
              currentTaxNumber === index + 1 &&
              currentTaxNumber !== TotalTaxNumberCount && (
                <AddTaxNumberButton
                  disabled={!(disableTaxButton + 1 > currentTaxNumber)}
                  onClick={() => handleAddTaxNumber(setCurrentTaxNumber)}
                  t={t}
                />
              )}
          </>
        );
      }
    } else {
      return <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />;
    }
    return <></>;
  });
}

function BillToAddressCard(props: BillToAddressCardProps) {
  const { isReview, store, errorMustContain, fields } = props;
  const {
    customer: {
      billTo: { address }
    }
  } = store;
  // const { address } = billTo;
  const { t } = useTranslation();
  const TotalTaxNumberCount = fields.filter(obj => obj.key.startsWith('taxNumber')).length;
  const backendTaxNumberCount = Object.keys(address).filter(key => key.startsWith('taxNumber')).length;
  const [currentTaxNumber, setCurrentTaxNumber] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  const [disableTaxButton, setDisableTaxButton] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
  useEffect(() => {
    const backendTaxNumberCount = Object.keys(address).filter(key => key.startsWith('taxNumber')).length;
    setDisableTaxButton(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
    setCurrentTaxNumber(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  }, [address, setCurrentTaxNumber]);
  const cardProps = {
    cardId: category,
    title: t('company.billTo.title'),
    subtitle: t('company.billTo.subtitle'),
    image: '/food/onboarding/assets/Company_Bill_To.svg',
    isReview,
    errorMustContain,
    t
  };
  const handleAddTaxNumber = () => {
    if (disableTaxButton + 1 > currentTaxNumber) {
      setCurrentTaxNumber(prevNum => prevNum + 1);
    }
  };

  return (
    <FormCard {...cardProps}>
      {renderTaxNumbers({
        currentTaxNumber,
        TotalTaxNumberCount,
        disableTaxButton,
        fields,
        controlMap,
        t,
        isReview,
        props,
        handleAddTaxNumber, // Pass handleAddTaxNumber as a prop
        setCurrentTaxNumber // Pass setCurrentTaxNumber as a prop
      })}
    </FormCard>
  );
}

export default BillToAddressCard;
